import React from 'react'
import { BusyButton } from '@ztechapps/webui.button'
import { useError, useToast } from '@ztechapps/common.hook'
import { useGenerateBlogSummary } from '../hooks/useGenerateBlogSummary'

export type GenerateSummaryButtonProps = {
  blogId: string
  onSummaryGenerated: (summary: string) => void
  disabled?: boolean
}

/**
 * Button component for generating AI-powered blog summary
 *
 * @description
 * Displays a button that triggers AI summary generation for a blog post.
 * Generated summary is directly applied without confirmation.
 *
 * @example
 * ```tsx
 * <GenerateSummaryButton
 *   blogId="blog-123"
 *   onSummaryGenerated={(summary) => setBlogSummary(summary)}
 * />
 * ```
 */
export const GenerateSummaryButton = ({ blogId, onSummaryGenerated, disabled = false }: GenerateSummaryButtonProps) => {
  const [generateSummary, , { loading }] = useGenerateBlogSummary()
  const { showSuccess, showError } = useToast()
  const { onError } = useError()

  const handleGenerateSummary = async () => {
    try {
      const result = await generateSummary({ input: { blogId } })
      const summary = result?.data?.generateBlogSummary ?? ''

      if (!summary) {
        showError({ message: 'No summary could be generated for this blog post.' })
        return
      }

      onSummaryGenerated(summary)
      showSuccess({ message: 'Summary generated and applied successfully' })
    } catch (err) {
      onError(err instanceof Error ? err : new Error(String(err)))
    }
  }

  return (
    <BusyButton
      icon="wand-magic-sparkles"
      text="Generate Summary"
      onClick={handleGenerateSummary}
      isBusy={loading}
      busyText="Generating..."
      disabled={disabled}
      title="Generate AI-powered summary for this blog post"
    />
  )
}
